import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useScreenSize } from 'src/global.hooks/useScreenSize'

export type Panel = string

export type PanelVisibilityState = {
  panels: Panel[]
  setPanelVisibility: (panel: Panel, visible: boolean) => void
  agentPanel?: Panel
  setAgentPanel: (agentPanel?: Panel) => void
  attached?: boolean
  setAttached: (attached: boolean) => void
}

const initialPanelVisibilityState: PanelVisibilityState = {
  panels: [],
  setPanelVisibility: () => {
    return
  },
  agentPanel: undefined,
  setAgentPanel: () => {
    return
  },
  attached: false,
  setAttached: () => {
    return
  },
}

const PanelVisibilityContext = createContext<PanelVisibilityState>(
  initialPanelVisibilityState,
)

export const usePanelVisibilityContext = (): PanelVisibilityState =>
  useContext(PanelVisibilityContext)

export type PanelVisibilityProviderProps = React.PropsWithChildren<{
  allPanels: Panel[]
}>

export const PanelVisibilityProvider = ({
  children,
  allPanels,
}: PanelVisibilityProviderProps): JSX.Element => {
  const { isDesktop, isMobile } = useScreenSize()
  const [agentPanel, setAgentPanel] = useState<Panel | undefined>(undefined)
  const [panels, setPanels] = useState<Panel[]>(
    isDesktop ? allPanels : [allPanels[0]],
  )
  const [attached, setAttached] = useState(false)

  const setPanelVisibility = useCallback(
    (panel: Panel, visible: boolean) => {
      const getNewPanels = (curr: Panel[]) => {
        const otherPanels = curr.filter((p) => p !== panel)
        if (visible) {
          if (!isDesktop) {
            return [panel]
          }
          return [...otherPanels, panel]
        } else {
          return isDesktop
            ? otherPanels
            : otherPanels.length
            ? [otherPanels[0]]
            : []
        }
      }

      setPanels((curr) => {
        const newPanels = getNewPanels(curr)
        return newPanels
      })
    },
    [isDesktop],
  )

  useEffect(() => {
    if (!isDesktop) {
      setPanels((curr) => {
        return !isMobile && curr.length >= 1 ? [curr[0]] : []
      })
    }
  }, [isDesktop, isMobile, allPanels])

  const value = useMemo(
    () => ({
      panels,
      setPanelVisibility,
      agentPanel,
      setAgentPanel,
      attached,
      setAttached,
    }),
    [
      panels,
      setPanelVisibility,
      agentPanel,
      setAgentPanel,
      attached,
      setAttached,
    ],
  )

  return (
    <PanelVisibilityContext.Provider value={value}>
      {children}
    </PanelVisibilityContext.Provider>
  )
}

export type PendingPrompt = {
  prompt: string
  questionId: string
  taskId: string
}

export type ChatStateValue = {
  pendingPrompt: PendingPrompt | undefined
  answerIdForPendingPrompt: string | undefined
  chatTitle: string | undefined
}

export type ChatID = string

type ChatStateData = Record<ChatID, ChatStateValue>

type ChatStateContextValue = {
  chatStateData: ChatStateData
  setChatStateData: React.Dispatch<React.SetStateAction<ChatStateData>>
}

const initialChatStateContext: ChatStateContextValue = {
  chatStateData: {},
  setChatStateData: () => {
    return
  },
}

const ChatStateContext = createContext<ChatStateContextValue>(
  initialChatStateContext,
)

const useChatStateContext = (): ChatStateContextValue =>
  useContext(ChatStateContext)

export const ChatStateProvider = ({
  children,
}: React.PropsWithChildren): JSX.Element => {
  const [chatStateData, setChatStateData] = useState<ChatStateData>({})

  const value = useMemo(
    () => ({
      chatStateData,
      setChatStateData,
    }),
    [chatStateData, setChatStateData],
  )

  return (
    <ChatStateContext.Provider value={value}>
      {children}
    </ChatStateContext.Provider>
  )
}

export type ChatState = {
  setPendingPrompt: (pendingPrompt?: PendingPrompt) => void
  pendingPrompt?: PendingPrompt
  setAnswerIdForPendingPrompt: (answerId?: string) => void
  answerIdForPendingPrompt?: string
  setChatTitle: (chatTitle: string) => void
  chatTitle?: string
}

// Hook to set pending prompt for any chat ID (useful when chat ID is not known at render time)
export const useSetPendingPromptForChat = (): ((
  chatId: ChatID,
  pendingPrompt?: PendingPrompt,
) => void) => {
  const { setChatStateData } = useChatStateContext()

  return useCallback(
    (chatId: ChatID, pendingPrompt?: PendingPrompt) => {
      setChatStateData((curr: ChatStateData) => ({
        ...curr,
        [chatId]: {
          ...curr[chatId],
          pendingPrompt,
          answerIdForPendingPrompt: undefined,
        },
      }))
    },
    [setChatStateData],
  )
}

export const useChatState = (chatId: ChatID): ChatState => {
  const { chatStateData, setChatStateData } = useChatStateContext()

  const setPendingPrompt = useCallback(
    (pendingPrompt?: PendingPrompt) => {
      setChatStateData((curr: ChatStateData) => ({
        ...curr,
        [chatId]: {
          ...curr[chatId],
          pendingPrompt,
          answerIdForPendingPrompt: undefined,
        },
      }))
    },
    [chatId, setChatStateData],
  )

  const setAnswerIdForPendingPrompt = useCallback(
    (answerId?: string) => {
      setChatStateData((curr: ChatStateData) => ({
        ...curr,
        [chatId]: {
          ...curr[chatId],
          answerIdForPendingPrompt: answerId,
        },
      }))
    },
    [chatId, setChatStateData],
  )

  const setChatTitle = useCallback(
    (chatTitle: string) => {
      setChatStateData((curr: ChatStateData) => ({
        ...curr,
        [chatId]: { ...curr[chatId], chatTitle },
      }))
    },
    [chatId, setChatStateData],
  )

  const pendingPrompt = useMemo(
    (): PendingPrompt | undefined => chatStateData[chatId]?.pendingPrompt,
    [chatStateData, chatId],
  )

  const answerIdForPendingPrompt = useMemo(
    (): string | undefined => chatStateData[chatId]?.answerIdForPendingPrompt,
    [chatStateData, chatId],
  )

  const chatTitle = useMemo(
    (): string | undefined => chatStateData[chatId]?.chatTitle,
    [chatStateData, chatId],
  )

  return useMemo(
    () => ({
      setPendingPrompt,
      pendingPrompt,
      setAnswerIdForPendingPrompt,
      answerIdForPendingPrompt,
      setChatTitle,
      chatTitle,
    }),
    [
      setPendingPrompt,
      pendingPrompt,
      setAnswerIdForPendingPrompt,
      answerIdForPendingPrompt,
      setChatTitle,
      chatTitle,
    ],
  )
}

type ChatAgentModeState = {
  isCopilotMode: boolean
}

const initialChatAgentModeState: ChatAgentModeState = {
  isCopilotMode: false,
}

const ChatAgentModeContext = createContext<ChatAgentModeState>(
  initialChatAgentModeState,
)

export const useChatAgentMode = (): ChatAgentModeState =>
  useContext(ChatAgentModeContext)

export const ChatAgentModeProvider = ({
  children,
  isCopilotMode = false,
}: React.PropsWithChildren<{ isCopilotMode?: boolean }>): JSX.Element => {
  const value = useMemo(
    () => ({
      isCopilotMode,
    }),
    [isCopilotMode],
  )

  return (
    <ChatAgentModeContext.Provider value={value}>
      {children}
    </ChatAgentModeContext.Provider>
  )
}
